import { User } from "../types/user";

export type ConfirmedOrderDetail = {
    id: number;
    price: number | string;
    quantity: number | string;
    product: { id?: number; name?: string; image?: string; shop?: { name?: string } | null };
};

export type ConfirmedOrder = {
    id: number;
    subtotal: number | string;
    tax: number | string;
    delivery: number | string;
    total: number | string;
    discount?: number | string | null;
    promocode?: string | null;
    order_collection_id?: number | null;
    payment?: { id: number; status: string };
    // 1 = cash or completed online payment
    payment_status?: number;
    date?: string;
    time?: { from: string; to: string };
    address?: { address?: string; mobile?: string; receiver_name?: string } | null;
    details: ConfirmedOrderDetail[];
};

const num = (v: unknown) => Number(Number(v || 0).toFixed(2));

// E.164 (+9665xxxxxxxx) as required by Google enhanced conversions
export function toE164(countryCode?: string, mobile?: string) {
    const code = (countryCode || "").replace(/\D/g, "");
    let phone = (mobile || "").replace(/\D/g, "");
    if (!phone) return undefined;
    if (code && phone.startsWith(code)) phone = phone.slice(code.length);
    phone = phone.replace(/^0+/, "");
    return `+${code}${phone}`;
}

function wasTracked(key: string) {
    try {
        return localStorage.getItem(key) === "1";
    } catch {
        return false;
    }
}

function markTracked(key: string) {
    try {
        localStorage.setItem(key, "1");
    } catch {}
}

// GA4 "purchase" ecommerce event + user provided data, pushed once per order
export function pushPurchase(
    orders: ConfirmedOrder[],
    user: User | null,
    currency: string,
) {
    const pending = orders.filter(
        (o) => o.payment_status === 1 && !wasTracked(`purchase_tracked_${o.id}`),
    );
    if (!pending.length) return [];

    window.dataLayer = window.dataLayer || [];
    const userData = user
        ? {
              email: user.email || undefined,
              phone_number: toE164(user.country_code, user.mobile),
              address: {
                  first_name: user.first_name || undefined,
                  last_name: user.last_name || undefined,
                  country: user.country_symbol || undefined,
              },
          }
        : undefined;

    pending.forEach((order) => {
        window.dataLayer.push({ ecommerce: null });
        window.dataLayer.push({
            event: "purchase",
            user_data: userData,
            ecommerce: {
                transaction_id: String(order.id),
                value: num(order.total),
                tax: num(order.tax),
                shipping: num(order.delivery),
                discount: num(order.discount),
                coupon: order.promocode || undefined,
                currency,
                items: order.details.map((detail) => ({
                    item_id: String(detail.product?.id ?? detail.id),
                    item_name: detail.product?.name,
                    item_brand: detail.product?.shop?.name,
                    price: num(detail.price),
                    quantity: Number(detail.quantity) || 1,
                })),
            },
        });
        markTracked(`purchase_tracked_${order.id}`);
    });

    return pending;
}
